import {
  AccountBalanceWalletOutlined,
  CreditCardOutlined,
  PaymentsOutlined,
  QrCode2Outlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import type { PaymentMethodKind, PublicPaymentMethod } from "../api/types";

const ICON_BY_KIND: Record<PaymentMethodKind, typeof CreditCardOutlined> = {
  card: CreditCardOutlined,
  wallet: AccountBalanceWalletOutlined,
  zimple: AccountBalanceWalletOutlined,
  pix: QrCode2Outlined,
  bank_transfer: PaymentsOutlined,
  cash_voucher: ReceiptLongOutlined,
  other: QrCode2Outlined,
};

interface Props {
  methods: PublicPaymentMethod[];
  value: string | null;
  disabled?: boolean;
  onChange: (platformId: string) => void;
}

/**
 * Selector de medio de pago. El comprador elige una modalidad (tarjeta, QR,
 * billetera, etc.); cada opción es un `platformId` del provider que se envía al
 * confirmar. Tras confirmar, el provider devuelve el QR o el link de pago.
 */
export function PaymentMethodSelector({ methods, value, disabled, onChange }: Props) {
  if (methods.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Elegí cómo querés pagar
      </Typography>
      <Stack spacing={1}>
        {methods.map((m) => {
          const Icon = ICON_BY_KIND[m.method] ?? QrCode2Outlined;
          const selected = value === m.platformId;
          return (
            <Box
              key={m.platformId}
              role="radio"
              aria-checked={selected}
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && onChange(m.platformId)}
              onKeyDown={(e) => {
                if (!disabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onChange(m.platformId);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                cursor: disabled ? "default" : "pointer",
                border: "1.5px solid",
                borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.12)",
                bgcolor: selected ? "rgba(37, 99, 235, 0.06)" : "transparent",
                opacity: disabled && !selected ? 0.6 : 1,
                transition: "border-color .12s, background-color .12s",
                "&:hover": disabled ? {} : { borderColor: "primary.main" },
              }}
            >
              <Icon fontSize="small" color={selected ? "primary" : "action"} />
              <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                {m.name}
              </Typography>
              <Box
                sx={{
                  ml: "auto",
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selected ? (
                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "primary.main" }} />
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
